import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Upload, FileSpreadsheet, ArrowUpDown, Search, X, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, Trash2, RotateCcw, CheckCircle2, Archive, Play, Eye,
  FolderKanban, Plus, Shield, Clock, AlertTriangle, Database, Loader2, Zap, SlidersHorizontal, RefreshCw, ExternalLink,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const statusBg = (s) => {
  if (s === "Completed") return "bg-emerald-500/10 text-emerald-600 border-emerald-500/30";
  if (s === "In Progress") return "bg-blue-500/10 text-blue-600 border-blue-500/30";
  return "bg-slate-100 text-slate-500 border-slate-300";
};

const ResAvatar = ({ name, email }) => {
  const initials = (name || email || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const hue = [...(email || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
      style={{ backgroundColor: `hsl(${hue}, 55%, 50%)` }}>{initials}</div>
  );
};

export default function TrackerAdmin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");

  // Users
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Snapshots
  const [snapshots, setSnapshots] = useState([]);

  // GP Projects
  const [tasks, setTasks] = useState([]);
  const [gpStats, setGpStats] = useState({});
  const [gpTotal, setGpTotal] = useState(0);
  const [gpTotalPages, setGpTotalPages] = useState(1);
  const [gpPage, setGpPage] = useState(1);
  const [gpLoading, setGpLoading] = useState(false);
  const [gpSearch, setGpSearch] = useState("");
  const [gpProject, setGpProject] = useState("");
  const [gpResource, setGpResource] = useState("");
  const [gpType, setGpType] = useState("");
  const [gpStatus, setGpStatus] = useState("");
  const [gpDueFilter, setGpDueFilter] = useState("");
  const [gpSeason, setGpSeason] = useState("");
  const [sortBy, setSortBy] = useState("main_title");
  const [sortDir, setSortDir] = useState("asc");
  const [selectedSnap, setSelectedSnap] = useState("");
  const [showColToggle, setShowColToggle] = useState(false);
  const [visibleCols, setVisibleCols] = useState({ project: true, season: true, episode: true, task: true, resource: true, language: true, due_date: true, duration: true, status: true, po: true });
  const toggleCol = (col) => setVisibleCols(prev => ({ ...prev, [col]: !prev[col] }));

  // Filters
  const [filterOpts, setFilterOpts] = useState({ projects: [], resources: [], task_types: [], languages: [], seasons: [] });

  // Stats
  const [stats, setStats] = useState({});

  // Sync to Database
  const [syncingToDb, setSyncingToDb] = useState(false);
  const [showSyncResult, setShowSyncResult] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);

  // Resource Centre
  const [rcData, setRcData] = useState(null);
  const [rcLoading, setRcLoading] = useState(false);
  const [rcSearch, setRcSearch] = useState("");

  // Fetch core data
  const fetchData = useCallback(async () => {
    try {
      const [actRes, snapRes, syncRes] = await Promise.all([
        axios.get(`${API}/tracker/admin/activity`, { headers: getAdminHeaders() }),
        axios.get(`${API}/tracker/admin/snapshots`, { headers: getAdminHeaders() }),
        axios.get(`${API}/tracker/admin/last-sync`, { headers: getAdminHeaders() }).catch(() => ({ data: {} })),
      ]);
      setStats(actRes.data);
      setUsers(actRes.data.users || []);
      setSnapshots(snapRes.data.snapshots || []);
      const active = snapRes.data.snapshots?.find(s => s.is_active);
      if (active && !selectedSnap) setSelectedSnap(active.id);
      setLastSync(syncRes.data.last_sync || null);
      setSyncHistory(syncRes.data.sync_history || []);
    } catch { toast.error("Failed to load admin data"); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch Resource Centre data when tab opens
  useEffect(() => {
    if (tab === "resource-centre" && !rcData) {
      setRcLoading(true);
      axios.get(`${API}/tracker/admin/resource-centre`, { headers: getAdminHeaders() })
        .then(res => setRcData(res.data))
        .catch(() => toast.error("Failed to load resource centre"))
        .finally(() => setRcLoading(false));
    }
  }, [tab]);

  // Fetch filters when snapshot changes
  useEffect(() => {
    if (!selectedSnap) return;
    axios.get(`${API}/tracker/admin/filters`, { params: { snapshot_id: selectedSnap }, headers: getAdminHeaders() })
      .then(r => setFilterOpts(r.data)).catch(() => {});
  }, [selectedSnap]);

  // Fetch GP projects
  const fetchGP = useCallback(async () => {
    if (!selectedSnap) return;
    setGpLoading(true);
    const params = { snapshot_id: selectedSnap, page: gpPage, page_size: 50, sort_by: sortBy, sort_dir: sortDir };
    if (gpSearch) params.search = gpSearch;
    if (gpProject) params.project = gpProject;
    if (gpResource) params.resource = gpResource;
    if (gpType) params.task_type = gpType;
    if (gpStatus) params.status = gpStatus;
    if (gpDueFilter) params.due_filter = gpDueFilter;
    if (gpSeason) params.season = gpSeason;
    try {
      const res = await axios.get(`${API}/tracker/admin/gp-projects`, { params, headers: getAdminHeaders() });
      setTasks(res.data.tasks || []);
      setGpStats(res.data.stats || {});
      setGpTotal(res.data.total || 0);
      setGpTotalPages(res.data.total_pages || 1);
    } catch { toast.error("Failed to load projects"); }
    setGpLoading(false);
  }, [selectedSnap, gpPage, sortBy, sortDir, gpSearch, gpProject, gpResource, gpType, gpStatus, gpDueFilter, gpSeason]);

  useEffect(() => { fetchGP(); }, [fetchGP]);
  useEffect(() => { setGpPage(1); }, [gpSearch, gpProject, gpResource, gpType, gpStatus, gpDueFilter, gpSeason, selectedSnap]);

  // Handlers
  const handleCreateUser = async () => {
    if (!newEmail || !newPassword) return toast.error("Email and password required");
    try {
      await axios.post(`${API}/tracker/users`, { email: newEmail, password: newPassword, display_name: newName }, { headers: getAdminHeaders() });
      toast.success(`Created tracker user: ${newEmail}`);
      setShowCreate(false); setNewName(""); setNewEmail(""); setNewPassword("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create user"); }
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Delete tracker user ${email}?`)) return;
    try { await axios.delete(`${API}/tracker/users/${encodeURIComponent(email)}`, { headers: getAdminHeaders() }); toast.success("User deleted"); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleToggleUser = async (email, newStatus) => {
    try { await axios.put(`${API}/tracker/users/${encodeURIComponent(email)}`, { status: newStatus }, { headers: getAdminHeaders() }); toast.success("Updated"); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleActivateSnap = async (id) => {
    try { await axios.put(`${API}/tracker/admin/snapshots/${id}/activate`, {}, { headers: getAdminHeaders() }); toast.success("Snapshot activated"); setSelectedSnap(id); fetchData(); }
    catch { toast.error("Failed"); }
  };

  const handleRestoreSnap = async (id) => {
    try { await axios.put(`${API}/tracker/admin/snapshots/${id}/restore`, {}, { headers: getAdminHeaders() }); toast.success("Snapshot restored"); fetchData(); }
    catch { toast.error("Failed"); }
  };

  const handlePermDeleteSnap = async (id) => {
    if (!window.confirm("Permanently delete this snapshot and all its tasks? This cannot be undone.")) return;
    try { await axios.delete(`${API}/tracker/admin/snapshots/${id}`, { headers: getAdminHeaders() }); toast.success("Permanently deleted"); fetchData(); }
    catch { toast.error("Failed"); }
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };
  const SortIcon = sortDir === "asc" ? ArrowUp : ArrowDown;
  const SH = ({ label, field }) => (
    <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 cursor-pointer hover:text-blue-500 transition-colors whitespace-nowrap"
      onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{label}{sortBy === field ? <SortIcon className="w-3 h-3 text-blue-500" /> : <ArrowUpDown className="w-3 h-3 opacity-30" />}</div>
    </th>
  );

  const hasGpFilters = gpSearch || gpProject || gpResource || gpType || gpStatus || gpDueFilter || gpSeason;
  const selectedSnapData = snapshots.find(s => s.id === selectedSnap);

  const handleSyncToDatabase = async () => {
    if (!window.confirm("Sync this snapshot to the main database?\n\nThis will:\n• Update task statuses (In Progress → Completed)\n• Add new tasks not yet in the database\n• Never duplicate or overwrite rates/amounts")) return;
    setSyncingToDb(true);
    try {
      const res = await axios.post(`${API}/tracker/admin/sync-to-database${selectedSnap ? `?snapshot_id=${selectedSnap}` : ""}`, {}, { headers: getAdminHeaders(), timeout: 120000 });
      setShowSyncResult(res.data);
      toast.success(`Synced: ${res.data.inserted} new, ${res.data.updated} updated, ${res.data.skipped} unchanged`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingToDb(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="admin-back-btn"><ChevronLeft className="w-5 h-5" /></button>
            <Shield className="w-5 h-5 text-blue-600" />
            <h1 className="text-lg font-bold text-slate-800">Tracker Monitor</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-[12px]" onClick={() => setShowCreate(true)} data-testid="create-tracker-user-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Create User
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-4 space-y-4">
        {/* KPI Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Tracker Users", val: stats.total_users || 0, icon: Users, color: "text-blue-600" },
            { label: "Snapshots", val: snapshots.length, icon: FileSpreadsheet, color: "text-violet-600" },
            { label: "Total Tasks", val: stats.total_tasks || 0, icon: FolderKanban, color: "text-emerald-600" },
            { label: "Active Snapshot", val: snapshots.find(s => s.is_active)?.name || "None", icon: Play, color: "text-amber-600" },
          ].map(k => (
            <div key={k.label} className="bg-white rounded-xl border p-3 flex items-center gap-3" data-testid={`admin-kpi-${k.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50", k.color)}><k.icon className="w-5 h-5" /></div>
              <div><p className="text-lg font-bold text-slate-800">{k.val}</p><p className="text-[10px] text-slate-500 uppercase tracking-wider">{k.label}</p></div>
            </div>
          ))}
        </div>

        {/* Tab Nav */}
        <div className="flex items-center gap-1 bg-white rounded-xl border p-1.5">
          {[
            { id: "projects", label: "GP Projects", icon: FolderKanban },
            { id: "resource-centre", label: "Resource Centre", icon: Users },
            { id: "snapshots", label: "Snapshot Archive", icon: Archive },
            { id: "users", label: "User Management", icon: Users },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={cn("flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-medium transition-all",
                tab === t.id ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50")}
              data-testid={`admin-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* ════════ TAB: GP PROJECTS ════════ */}
        {tab === "projects" && (
          <div className="space-y-3" data-testid="admin-gp-projects">
            {/* Snapshot selector + stats */}
            <div className="bg-white rounded-xl border p-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Snapshot:</span>
                <Select value={selectedSnap} onValueChange={setSelectedSnap}>
                  <SelectTrigger className="h-8 w-64 text-[12px]" data-testid="admin-snapshot-selector"><SelectValue placeholder="Select snapshot" /></SelectTrigger>
                  <SelectContent>
                    {snapshots.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        <div className="flex items-center gap-2">
                          {s.archived && <Archive className="w-3 h-3 text-amber-500" />}
                          {s.is_active && <Play className="w-3 h-3 text-emerald-500" />}
                          <span>{s.name || s.filename || s.id.slice(0, 8)}</span>
                          <span className="text-slate-400 text-[10px]">({s.task_count} tasks)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSnapData && (
                  <div className="flex items-center gap-3 ml-auto">
                    {selectedSnapData.archived && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-600 border-amber-300">Archived</Badge>}
                    {selectedSnapData.is_active && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-600 border-emerald-300">Active</Badge>}
                    <span className="text-[11px] text-slate-400">{selectedSnapData.task_count} tasks</span>
                    <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white gap-1" onClick={handleSyncToDatabase} disabled={syncingToDb} data-testid="admin-sync-to-db-btn">
                      {syncingToDb ? <Loader2 className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />}
                      {syncingToDb ? "Syncing..." : "Sync to Database"}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/tracker/admin/create-public-link${selectedSnap ? `?snapshot_id=${selectedSnap}` : ""}`, {}, { headers: getAdminHeaders() });
                        const url = `${window.location.origin}/public/tracker/${res.data.token}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Public link copied to clipboard!");
                      } catch { toast.error("Failed to create link"); }
                    }} data-testid="create-public-link-btn">
                      <ExternalLink className="w-3 h-3" />Share Link
                    </Button>
                  </div>
                )}
              </div>

              {/* Last Sync Info */}
              {lastSync && (
                <div className="flex items-center gap-4 mt-2.5 px-2 py-2 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="last-sync-info">
                  <Database className="w-4 h-4 text-emerald-600 shrink-0" />
                  <div className="flex items-center gap-4 flex-wrap text-[11px]">
                    <span className="font-semibold text-emerald-800">Last Synced:</span>
                    <span className="text-emerald-700">{new Date(lastSync.committed_at).toLocaleString()}</span>
                    <span className="text-emerald-600">Snapshot: {lastSync.snapshot_name}</span>
                    <div className="flex items-center gap-2 text-emerald-700">
                      <span className="font-medium">{lastSync.inserted} added</span>
                      <span>·</span>
                      <span className="font-medium">{lastSync.updated} updated</span>
                      <span>·</span>
                      <span>{lastSync.skipped} unchanged</span>
                      {lastSync.status_changes?.length > 0 && <><span>·</span><span className="font-medium">{lastSync.status_changes.length} status changes</span></>}
                    </div>
                  </div>
                  {syncHistory.length > 1 && (
                    <details className="ml-auto text-[10px]">
                      <summary className="cursor-pointer text-emerald-600 hover:text-emerald-800 font-medium">History ({syncHistory.length})</summary>
                      <div className="absolute right-4 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-50 w-80">
                        {syncHistory.map((h, i) => (
                          <div key={h.id || i} className="flex items-center gap-2 px-2 py-1.5 text-[10px] border-b border-slate-50 last:border-0">
                            <span className="text-slate-400 w-28 shrink-0">{new Date(h.committed_at).toLocaleString()}</span>
                            <span className="text-slate-600 truncate">{h.snapshot_name}</span>
                            <span className="ml-auto text-emerald-600 font-medium whitespace-nowrap">+{h.inserted} / ~{h.updated} / ={h.skipped}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* GP Stats */}
              <div className="grid grid-cols-5 gap-2 mt-3">
                {[
                  { label: "Total", val: gpTotal, color: "text-blue-600" },
                  { label: "Completed", val: gpStats.completed || 0, color: "text-emerald-600" },
                  { label: "In Progress", val: gpStats.in_progress || 0, color: "text-amber-600" },
                  { label: "Pending", val: gpStats.pending || 0, color: "text-slate-500" },
                  { label: "Completion", val: `${gpStats.completion_pct || 0}%`, color: "text-violet-600" },
                ].map(c => (
                  <div key={c.label} className="bg-slate-50 rounded-lg p-2 text-center">
                    <p className={cn("text-sm font-bold", c.color)}>{c.val}</p>
                    <p className="text-[9px] text-slate-400 uppercase">{c.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl border p-3 flex items-center gap-2 flex-wrap">
              <div className="relative w-44 shrink-0"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" /><Input className="h-8 pl-8 text-[12px]" placeholder="Search tasks..." value={gpSearch} onChange={e => setGpSearch(e.target.value)} data-testid="admin-gp-search" /></div>
              {filterOpts.projects?.length > 0 && (
                <Select value={gpProject || "all"} onValueChange={v => setGpProject(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-8 w-40 text-[12px]" data-testid="admin-gp-filter-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="all" value="all">All Projects</SelectItem>, ...filterOpts.projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)]}</SelectContent>
                </Select>
              )}
              {filterOpts.resources?.length > 0 && (
                <Select value={gpResource || "all"} onValueChange={v => setGpResource(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-8 w-36 text-[12px]" data-testid="admin-gp-filter-resource"><SelectValue placeholder="Resource" /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="all" value="all">All Resources</SelectItem>, ...filterOpts.resources.map(r => <SelectItem key={r.email} value={r.email}>{r.name}</SelectItem>)]}</SelectContent>
                </Select>
              )}
              {filterOpts.task_types?.length > 0 && (
                <Select value={gpType || "all"} onValueChange={v => setGpType(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-8 w-28 text-[12px]" data-testid="admin-gp-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>{[<SelectItem key="all" value="all">All Types</SelectItem>, ...filterOpts.task_types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)]}</SelectContent>
                </Select>
              )}
              <Select value={gpStatus || "all"} onValueChange={v => setGpStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 w-28 text-[12px]" data-testid="admin-gp-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="Pending">Pending</SelectItem></SelectContent>
              </Select>
              <Select value={gpDueFilter || "all"} onValueChange={v => setGpDueFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 w-32 text-[12px]" data-testid="admin-gp-filter-due"><SelectValue placeholder="Due Date" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Dates</SelectItem><SelectItem value="overdue">Overdue</SelectItem><SelectItem value="this_week">This Week</SelectItem><SelectItem value="next_week">Next Week</SelectItem><SelectItem value="no_date">No Due Date</SelectItem></SelectContent>
              </Select>
              {filterOpts.seasons?.length > 0 && (
                <Select value={gpSeason || "all"} onValueChange={v => setGpSeason(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-8 w-28 text-[12px]" data-testid="admin-gp-filter-season"><SelectValue placeholder="Season" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Seasons</SelectItem>{filterOpts.seasons.map(s => <SelectItem key={s} value={s}>Season {s}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <div className="relative ml-auto">
                <Button variant="ghost" size="sm" className="h-8 text-[11px] text-slate-500" onClick={() => setShowColToggle(!showColToggle)} data-testid="admin-col-toggle">
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />Columns
                </Button>
                {showColToggle && (
                  <div className="absolute right-0 top-9 z-50 w-44 bg-white rounded-lg border shadow-xl p-2 space-y-0.5" data-testid="admin-col-menu">
                    {[["project","Project"],["season","Season"],["episode","Episode"],["task","Task"],["resource","Resource"],["language","Language"],["due_date","Due Date"],["duration","Duration"],["status","Status"],["po","PO"]].map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-[11px] text-slate-700 hover:bg-slate-100">
                        <input type="checkbox" checked={visibleCols[key]} onChange={() => toggleCol(key)} className="rounded border-slate-300 w-3 h-3" />
                        {label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {hasGpFilters && (
                <Button variant="ghost" size="sm" className="h-8 text-[11px] text-red-500" onClick={() => { setGpSearch(""); setGpProject(""); setGpResource(""); setGpType(""); setGpStatus(""); setGpDueFilter(""); setGpSeason(""); }} data-testid="admin-gp-clear"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
              )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 text-left">#</th>
                      {visibleCols.project && <SH label="Project" field="main_title" />}
                      {visibleCols.season && <SH label="Season" field="season_number" />}
                      {visibleCols.episode && <SH label="Episode" field="episode_title" />}
                      {visibleCols.task && <SH label="Task" field="task_type" />}
                      {visibleCols.resource && <SH label="Resource" field="resource_name" />}
                      {visibleCols.language && <SH label="Language" field="language" />}
                      {visibleCols.due_date && <SH label="Due Date" field="due_date" />}
                      {visibleCols.duration && <SH label="Duration" field="duration" />}
                      {visibleCols.status && <SH label="Status" field="status" />}
                      {visibleCols.po && <SH label="PO" field="po_number" />}
                    </tr>
                  </thead>
                  <tbody>
                    {gpLoading ? (
                      <tr><td colSpan={Object.values(visibleCols).filter(Boolean).length + 1} className="text-center py-10"><div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
                    ) : tasks.length === 0 ? (
                      <tr><td colSpan={Object.values(visibleCols).filter(Boolean).length + 1} className="text-center py-10 text-sm text-slate-400">No tasks found</td></tr>
                    ) : tasks.map((task, i) => (
                      <tr key={task.id} className={cn("border-b hover:bg-slate-50 transition-colors", task.is_overdue && "bg-red-50/50")} data-testid={`admin-gp-row-${task.id}`}>
                        <td className="px-3 py-2 text-[11px] text-slate-400">{(gpPage - 1) * 50 + i + 1}</td>
                        {visibleCols.project && <td className="px-3 py-2"><p className="text-[12px] font-medium text-slate-700 truncate max-w-[160px]">{task.main_title}</p></td>}
                        {visibleCols.season && <td className="px-3 py-2 text-[12px] text-slate-500">{task.season_number ? `S${task.season_number}` : "-"}</td>}
                        {visibleCols.episode && <td className="px-3 py-2 text-[12px] text-slate-500 truncate max-w-[140px]">{task.episode_title || "-"}</td>}
                        {visibleCols.task && <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{task.task_type || "-"}</Badge></td>}
                        {visibleCols.resource && <td className="px-3 py-2"><div className="flex items-center gap-1.5"><ResAvatar name={task.resource_name} email={task.resource} /><span className="text-[12px] text-slate-700 truncate max-w-[100px]">{task.resource_name}</span></div></td>}
                        {visibleCols.language && <td className="px-3 py-2 text-[12px] text-slate-500">{task.language || "-"}</td>}
                        {visibleCols.due_date && <td className={cn("px-3 py-2 text-[12px]", task.is_overdue ? "text-red-500 font-semibold" : "text-slate-500")}>{task.due_date || "-"}</td>}
                        {visibleCols.duration && <td className="px-3 py-2 text-[12px] text-slate-500">{task.duration || "-"}</td>}
                        {visibleCols.status && <td className="px-3 py-2"><Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", statusBg(task.status))}>{task.status}</Badge></td>}
                        {visibleCols.po && <td className="px-3 py-2 text-[11px] text-slate-400">{task.po_number || "-"}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {gpTotalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <span className="text-[11px] text-slate-400">Showing {(gpPage - 1) * 50 + 1}-{Math.min(gpPage * 50, gpTotal)} of {gpTotal}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={gpPage <= 1} onClick={() => setGpPage(p => p - 1)} data-testid="admin-gp-prev"><ChevronLeft className="w-3.5 h-3.5" /> Prev</Button>
                    <span className="text-[11px] text-slate-500 px-2">Page {gpPage} of {gpTotalPages}</span>
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={gpPage >= gpTotalPages} onClick={() => setGpPage(p => p + 1)} data-testid="admin-gp-next">Next <ChevronRight className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════ TAB: RESOURCE CENTRE ════════ */}
        {tab === "resource-centre" && (
          <div className="space-y-3" data-testid="admin-resource-centre">
            {/* KPI Bar */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border p-3 text-center">
                <p className="text-lg font-bold text-blue-600">{rcData?.total_resources || 0}</p>
                <p className="text-[9px] text-slate-500 uppercase">Total Resources</p>
              </div>
              <div className="bg-white rounded-xl border p-3 text-center">
                <p className="text-lg font-bold text-emerald-600">{rcData?.total_translator_completed || 0}</p>
                <p className="text-[9px] text-slate-500 uppercase">Marked Complete</p>
              </div>
              <div className="bg-white rounded-xl border p-3 text-center">
                <p className="text-lg font-bold text-red-600">{rcData?.total_issues || 0}</p>
                <p className="text-[9px] text-slate-500 uppercase">Issues Flagged</p>
              </div>
              <div className="bg-white rounded-xl border p-3 text-center flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={() => { setRcData(null); setRcLoading(true); axios.get(`${API}/tracker/admin/resource-centre`, { headers: getAdminHeaders() }).then(r => setRcData(r.data)).finally(() => setRcLoading(false)); }}>
                  <RefreshCw className="w-3 h-3 mr-1" />Refresh
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl border p-3">
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input className="h-8 pl-8 text-[12px]" placeholder="Search resource..." value={rcSearch} onChange={e => setRcSearch(e.target.value)} data-testid="rc-centre-search" />
              </div>
            </div>

            {/* Resource Table */}
            <div className="bg-white rounded-xl border overflow-hidden">
              {rcLoading ? (
                <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">#</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-400">Resource</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Tasks</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Projects</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">DB Status</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Translator Status</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Issues</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Completion</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-400">Last Update</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(rcData?.resources || []).filter(r => !rcSearch || r.name.toLowerCase().includes(rcSearch.toLowerCase()) || r.email.toLowerCase().includes(rcSearch.toLowerCase())).map((r, i) => (
                        <tr key={r.email} className={cn("border-b hover:bg-slate-50 transition-colors", r.issue_flagged > 0 && "bg-red-50/30")}>
                          <td className="px-3 py-2 text-[11px] text-slate-400">{i + 1}</td>
                          <td className="px-3 py-2">
                            <p className="text-[12px] font-medium text-slate-800">{r.name}</p>
                            <p className="text-[10px] text-slate-400">{r.email}</p>
                          </td>
                          <td className="px-3 py-2 text-center text-[12px] font-semibold text-slate-700">{r.total_tasks}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="text-[11px] text-slate-600" title={r.projects?.join(", ")}>{r.project_count} projects</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1.5 text-[10px]">
                              <span className="text-emerald-600 font-medium">{r.completed} done</span>
                              <span className="text-slate-300">|</span>
                              <span className="text-amber-600">{r.in_progress} wip</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1.5 text-[10px]">
                              {r.translator_completed > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px]">{r.translator_completed} done</Badge>}
                              {r.translator_in_progress > 0 && <Badge className="bg-blue-100 text-blue-700 border-0 text-[9px]">{r.translator_in_progress} wip</Badge>}
                              {!r.translator_completed && !r.translator_in_progress && <span className="text-slate-300">—</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {r.issue_flagged > 0 ? (
                              <Badge className="bg-red-100 text-red-700 border-0 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />{r.issue_flagged}</Badge>
                            ) : <span className="text-slate-300 text-[11px]">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${r.completion_pct}%` }} />
                              </div>
                              <span className="text-[10px] text-slate-500 w-8">{r.completion_pct}%</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center text-[10px] text-slate-400">
                            {r.last_translator_update ? new Date(r.last_translator_update).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                      {(rcData?.resources || []).length === 0 && !rcLoading && (
                        <tr><td colSpan={9} className="text-center py-10 text-sm text-slate-400">No resource data available</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════ TAB: SNAPSHOT ARCHIVE ════════ */}
        {tab === "snapshots" && (
          <div className="bg-white rounded-xl border overflow-hidden" data-testid="admin-snapshots">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Archive className="w-4 h-4 text-violet-500" /> All Snapshots ({snapshots.length})</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">Archived snapshots are preserved when deleted by tracker users</p>
            </div>
            <div className="divide-y">
              {snapshots.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">No snapshots uploaded yet</div>
              ) : snapshots.map(s => (
                <div key={s.id} className={cn("px-4 py-3 flex items-center gap-4 transition-colors hover:bg-slate-50", s.archived && "bg-amber-50/30")} data-testid={`admin-snap-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-[13px] font-medium text-slate-700">{s.name || s.filename || s.id.slice(0, 12)}</p>
                      {s.is_active && <Badge className="text-[9px] bg-emerald-500 text-white">Active</Badge>}
                      {s.archived && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-600 border-amber-300">Archived</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[10px] text-slate-400">{s.task_count} tasks</span>
                      <span className="text-[10px] text-slate-400">Uploaded: {s.uploaded_at ? new Date(s.uploaded_at).toLocaleDateString() : "N/A"}</span>
                      {s.uploaded_by && <span className="text-[10px] text-slate-400">by {s.uploaded_by}</span>}
                      {s.archived_at && <span className="text-[10px] text-amber-500">Archived: {new Date(s.archived_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <TooltipProvider><Tooltip><TooltipTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => { setSelectedSnap(s.id); setTab("projects"); }} data-testid={`admin-snap-view-${s.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger><TooltipContent>View tasks</TooltipContent></Tooltip></TooltipProvider>

                    {!s.is_active && !s.archived && (
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-emerald-600" onClick={() => handleActivateSnap(s.id)} data-testid={`admin-snap-activate-${s.id}`}>
                          <Play className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Set as active</TooltipContent></Tooltip></TooltipProvider>
                    )}
                    {s.is_active && (
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-emerald-600" disabled>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Currently active</TooltipContent></Tooltip></TooltipProvider>
                    )}
                    {s.archived && (
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-blue-600" onClick={() => handleRestoreSnap(s.id)} data-testid={`admin-snap-restore-${s.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Restore from archive</TooltipContent></Tooltip></TooltipProvider>
                    )}
                    <TooltipProvider><Tooltip><TooltipTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => handlePermDeleteSnap(s.id)} data-testid={`admin-snap-delete-${s.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TooltipTrigger><TooltipContent>Permanently delete</TooltipContent></Tooltip></TooltipProvider>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════════ TAB: USER MANAGEMENT ════════ */}
        {tab === "users" && (
          <div className="bg-white rounded-xl border overflow-hidden" data-testid="admin-users">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> Tracker Users ({users.length})</h3>
            </div>
            <div className="divide-y">
              {users.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">No users created yet</div>
              ) : users.map(u => (
                <div key={u.email} className="px-4 py-3 flex items-center gap-3" data-testid={`admin-user-${u.email}`}>
                  <ResAvatar name={u.display_name} email={u.email} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-slate-700">{u.display_name || u.email}</p>
                    <p className="text-[10px] text-slate-400">{u.email}</p>
                  </div>
                  <Badge variant="outline" className={cn("text-[10px]", u.status === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-300" : "bg-red-50 text-red-500 border-red-300")}>{u.status || "active"}</Badge>
                  <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => handleToggleUser(u.email, u.status === "active" ? "disabled" : "active")}>
                    {u.status === "active" ? "Disable" : "Enable"}
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteUser(u.email)} data-testid={`admin-delete-user-${u.email}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Tracker User</DialogTitle>
            <DialogDescription>Create a new user who can access the standalone project tracker.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input placeholder="Display Name" value={newName} onChange={e => setNewName(e.target.value)} data-testid="new-tracker-name" />
            <Input type="email" placeholder="Email" value={newEmail} onChange={e => setNewEmail(e.target.value)} data-testid="new-tracker-email" />
            <Input type="password" placeholder="Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} data-testid="new-tracker-password" />
            <Button className="w-full" onClick={handleCreateUser} data-testid="submit-create-tracker-user">Create User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sync Result Dialog */}
      <Dialog open={!!showSyncResult} onOpenChange={() => setShowSyncResult(null)}>
        <DialogContent className="max-w-md" data-testid="admin-sync-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-emerald-500" />Sync Complete</DialogTitle>
            <DialogDescription>Snapshot synced to main adjustment database</DialogDescription>
          </DialogHeader>
          {showSyncResult && (
            <div className="space-y-3 mt-2">
              <p className="text-xs text-slate-500">Snapshot: {showSyncResult.snapshot_name}</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg p-3 text-center bg-emerald-50">
                  <p className="text-lg font-bold text-emerald-600">{showSyncResult.inserted}</p>
                  <p className="text-[10px] text-emerald-600">New Added</p>
                </div>
                <div className="rounded-lg p-3 text-center bg-blue-50">
                  <p className="text-lg font-bold text-blue-600">{showSyncResult.updated}</p>
                  <p className="text-[10px] text-blue-600">Updated</p>
                </div>
                <div className="rounded-lg p-3 text-center bg-slate-50">
                  <p className="text-lg font-bold text-slate-500">{showSyncResult.skipped}</p>
                  <p className="text-[10px] text-slate-500">Unchanged</p>
                </div>
              </div>
              {showSyncResult.status_changes_count > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-700 mb-1.5">Status Changes ({showSyncResult.status_changes_count})</p>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {(showSyncResult.status_changes || []).map((sc, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded text-[11px]">
                        <span className="font-mono text-slate-400">{sc.po_number}</span>
                        <span className="text-red-500 line-through">{sc.old}</span>
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                        <span className="text-emerald-600 font-medium">{sc.new}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button className="w-full" onClick={() => setShowSyncResult(null)}>Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
